<template>
  <v-dialog v-model="visible" fullscreen hide-overlay transition="dialog-bottom-transition">
    <v-card class="declarations-page">
      <div class="glass-header-eracun">
        <div class="header-pattern"></div>
        <div class="header-title">
          <v-icon color="white" class="mr-2">mdi-view-column-outline</v-icon>
          <h2 class="headline-eracun white--text">Template deklaracije</h2>
        </div>
        <v-btn icon class="close-btn" @click="close"><v-icon color="white">mdi-close</v-icon></v-btn>
      </div>
      <v-card-text class="pt-5">
        <v-row class="justify-end pr-3 pb-2">
          <v-btn class="search-btn" height="38" @click="openAddField()">
            <v-icon left small>mdi-plus</v-icon>Dodaj polje
          </v-btn>
        </v-row>
        <v-row>
          <v-col cols="6" v-for="col in [1, 2]" :key="col">
            <h4 class="column-title mb-2">Kolona {{ col }}</h4>
            <draggable v-model="columnFields[col]" group="declarationFields" class="column-dropzone" @change="onReorder">
              <div v-if="columnFields[col].length === 0" class="column-dropzone-empty">
                <v-icon size="32" color="#CBD5E1">mdi-tray-arrow-down</v-icon>
                <span>Povucite polja ovdje kako biste ih dodali u ovu kolonu</span>
              </div>
              <div v-for="field in columnFields[col]" :key="field.key" class="declaration-row d-flex align-center">
                <v-icon class="mr-2 drag-handle">mdi-drag</v-icon>
                <div class="flex-grow-1">
                  <div class="row-label">{{ field.label }}</div>
                  <div class="row-meta">
                    {{ field.type === 'attribute' ? 'Atribut: ' + attributeLabel(field.attribute_field) : 'Ručni unos' }}
                    <span v-if="field.default_value"> · default: {{ field.default_value }}</span>
                    <span v-if="!field.printaj"> · ne printa se</span>
                  </div>
                </div>
                <v-btn icon @click="openEditField(field)"><v-icon color="#3989C9">mdi-pencil</v-icon></v-btn>
                <v-btn icon @click="removeField(field)"><v-icon color="#ef4444">mdi-delete</v-icon></v-btn>
              </div>
            </draggable>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="d-flex justify-end pb-5 pr-6">
        <v-btn text class="cancel-btn-eracun" @click="close">Odustani</v-btn>
        <v-btn class="save-btn" height="45" :loading="saving" :disabled="saving" @click="save">
          Spremi predložak
        </v-btn>
      </v-card-actions>
    </v-card>
    <add-declaration-field ref="addField" @save="onFieldSaved"></add-declaration-field>
  </v-dialog>
</template>
<script>
import draggable from 'vuedraggable'
import { v4 as uuidv4 } from 'uuid'
import { clone } from '@/plugins/utils'
import {
  ITEM_ATTRIBUTE_OPTIONS,
  getTemplate,
  saveTemplateFields,
  canDeleteTemplateField,
  getFieldValueOverrides,
  forceApplyValueToAll
} from './declarationsService.js'
import AddDeclarationField from './AddDeclarationField.vue'
import state from '@/state'

export default {
  name: 'DeclarationTemplateBuilder',
  inject: ['showMsgBox', 'showLoader', 'hideLoader', 'confirm', 'confirmClose'],
  components: { draggable, AddDeclarationField },
  data: () => ({
    visible: false,
    saving: false,
    fields: [],
    originalFields: [],
    removedKeys: [],
    editingKey: null,
    columnFields: { 1: [], 2: [] }
  }),
  methods: {
    rebuildColumns () {
      const byColumn = { 1: [], 2: [] }
      this.fields
        .slice()
        .sort((a, b) => a.order - b.order)
        .forEach(f => byColumn[f.column].push(f))
      this.columnFields = byColumn
    },
    attributeLabel (value) {
      const option = ITEM_ATTRIBUTE_OPTIONS.find(o => o.value === value)
      return option ? option.label : value
    },
    async open () {
      this.showLoader()
      const template = await getTemplate(state.getCurrentCompany().id)
      this.fields = template.fields || []
      this.originalFields = clone(this.fields)
      this.removedKeys = []
      this.rebuildColumns()
      this.visible = true
      this.hideLoader()
    },
    close () {
      this.visible = false
    },
    openAddField () {
      this.editingKey = null
      this.$refs.addField.open()
    },
    openEditField (field) {
      this.editingKey = field.key
      this.$refs.addField.open(field)
    },
    onFieldSaved (fieldData) {
      if (this.editingKey) {
        const idx = this.fields.findIndex(f => f.key === this.editingKey)
        const existing = this.fields[idx]
        this.fields.splice(idx, 1, { ...existing, ...fieldData, order: existing.order })
      } else {
        const order = this.columnFields[fieldData.column].length
        this.fields.push({ key: uuidv4(), order, ...fieldData })
      }
      this.editingKey = null
      this.rebuildColumns()
    },
    onReorder () {
      // Reassign order per column after drag-and-drop
      ;[1, 2].forEach(col => {
        this.columnFields[col].forEach((field, idx) => {
          const target = this.fields.find(f => f.key === field.key)
          target.order = idx
          target.column = col
        })
      })
    },
    async removeField (field) {
      const check = await canDeleteTemplateField(state.getCurrentCompany().id, field.key)
      if (!check.allowed) {
        const names = check.blockingItems.map(i => `${i.name}${i.item_code ? ' (' + i.item_code + ')' : ''}`).join(', ')
        this.showMsgBox({
          text: `Ovo polje se koristi na artiklima: ${names}. Otvorite svaki artikl i ručno uklonite/poništite vrijednost prije brisanja iz predloška.`,
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
        return
      }
      this.fields = this.fields.filter(f => f.key !== field.key)
      this.removedKeys.push(field.key)
      this.rebuildColumns()
    },
    async save () {
      this.saving = true
      const companyId = state.getCurrentCompany().id
      try {
        // Fields whose default_value changed for an already-existing manual field —
        // items with their own stored value (override or direct entry) won't pick up
        // the new default automatically, so ask whether to force it onto them too.
        const changedDefaults = this.fields.filter(f => {
          if (f.type !== 'manual' || !f.default_value) return false
          const original = this.originalFields.find(of => of.key === f.key)
          return original && original.default_value !== f.default_value
        })

        const forceApplies = []
        for (const field of changedDefaults) {
          const overrides = await getFieldValueOverrides(companyId, field.key)
          if (overrides.length === 0) continue
          const resp = await this.confirm({
            title: 'Primijeniti novu vrijednost svima?',
            message: `Polje "${field.label}" ima posebno upisanu vrijednost na ${overrides.length} ${overrides.length === 1 ? 'artiklu' : 'artikala'}. Želite li novu default vrijednost "${field.default_value}" primijeniti i na te artikle?`,
            options: { toolbar: true, width: 480, confirmText: 'DA', cancelText: 'NE' }
          })
          this.confirmClose()
          if (resp) forceApplies.push(field)
        }

        await saveTemplateFields(companyId, this.fields, this.removedKeys)
        for (const field of forceApplies) {
          await forceApplyValueToAll(companyId, field.key, field.default_value)
        }

        this.removedKeys = []
        this.$emit('success')
        this.visible = false
      } catch (err) {
        this.showMsgBox({
          text: err && err.message ? err.message : 'Greška prilikom spremanja predloška',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
      this.saving = false
    }
  }
}
</script>
<style scoped>
.declarations-page {
  background: #F5F7FA;
}
.glass-header-eracun {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: linear-gradient(135deg, rgba(0, 179, 190, 0.5) 0%, rgba(57, 137, 201, 0.45) 50%, rgba(97, 84, 164, 0.5) 100%) !important;
  position: relative !important;
}
.header-pattern {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.3;
  z-index: 0;
}
.header-title {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
}
.headline-eracun {
  font-size: 20px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  margin: 0 !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.close-btn {
  background: rgba(255, 255, 255, 0.15);
  position: relative;
  z-index: 1;
}
.column-title {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.column-dropzone {
  display: block;
  background: rgba(249, 250, 251, 0.6);
  border: 2px dashed #E2E8F0;
  border-radius: 16px;
  min-height: 380px;
  padding: 16px;
}
.column-dropzone-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 100%;
  min-height: 340px;
  color: #94A3B8;
  font-size: 13px;
  text-align: center;
  padding: 0 24px;
}
.declaration-row {
  border: 1px solid #EEF2F5;
  border-left: 3px solid #00BCD4;
  border-radius: 6px;
  background: #fff;
  padding: 8px 10px;
  margin-bottom: 8px;
  cursor: grab;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.declaration-row:active {
  cursor: grabbing;
}
.declaration-row:hover {
  border-color: rgba(0, 188, 212, 0.5);
  background: #F8FAFB;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}
.drag-handle {
  cursor: grab;
  color: #94A3B8;
}
.cancel-btn-eracun {
  color: rgba(70, 70, 80, 0.7) !important;
  text-transform: none !important;
  font-weight: 500 !important;
}
.row-label {
  font-weight: 600;
  color: #263238;
}
.row-meta {
  font-size: 12px;
  color: #94A3B8;
}
.search-btn, .save-btn {
  background: rgba(0, 188, 212, 0.08);
  color: #00838F;
  border: 1.2px solid #00b3be;
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  font-size: 13px;
  box-shadow: none;
}
.search-btn:hover, .save-btn:hover {
  background: rgba(0, 179, 190, 0.12);
}
</style>
